import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useUser } from './UserContext';
import {
  catererMessagingOperations,
  catererRealtimeOperations,
  orderOperations,
} from '@/lib/supabase';

const noop = () => {};
const NotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  isOpen: false,
  toasts: [],
  addNotification: noop,
  addToast: noop,
  removeToast: noop,
  markAsRead: noop,
  markAllAsRead: noop,
  clearAll: noop,
  toggleOpen: noop,
  closePanel: noop,
  fetchUnreadCount: noop,
  pushPermission: null,
  requestPushPermission: noop,
});

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { currentUserId, currentCatererId } = useUser();
  const isAuthenticated = Boolean(currentUserId);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [pushPermission, setPushPermission] = useState(
    () => (typeof Notification !== 'undefined' ? Notification.permission : null)
  );
  const seededRef = useRef(false);
  const clearedAtRef = useRef(0);

  const getClearKey = useCallback(
    () => (currentUserId ? `caterer_notifications_cleared_at_${currentUserId}` : null),
    [currentUserId]
  );

  const loadClearedAt = useCallback(() => {
    const key = getClearKey();
    if (!key || typeof window === 'undefined') return 0;
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }, [getClearKey]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const fetchUnreadCount = useCallback(async () => {
    if (!currentUserId) return;
    try {
      await catererMessagingOperations.getUnreadCount(currentUserId);
    } catch (err) {
      console.error('[Notifications] Error fetching unread count:', err);
    }
  }, [currentUserId]);

  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    const duration = toast.duration ?? 5000;
    const newToast = {
      id,
      type: toast.type || 'info',
      title: toast.title,
      message: toast.message,
      duration,
      action: toast.action,
    };
    setToasts((prev) => [...prev, newToast]);
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const requestPushPermission = useCallback(async () => {
    if (typeof window === 'undefined' || !window.Notification) return null;
    try {
      const permission = await window.Notification.requestPermission();
      setPushPermission(permission);
      return permission;
    } catch (_) {
      return null;
    }
  }, []);

  // Auto-request push notifications when caterer is authenticated (enable by default)
  useEffect(() => {
    if (!isAuthenticated || !currentUserId) return;
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      requestPushPermission();
    }
  }, [isAuthenticated, currentUserId, requestPushPermission]);

  const addNotification = useCallback(
    (notification, options = {}) => {
      const { silent = false } = options;
      const timestamp = notification?.timestamp ? new Date(notification.timestamp) : new Date();
      const clearedAt = clearedAtRef.current || 0;
      if (clearedAt && timestamp.getTime() <= clearedAt) {
        return;
      }
      const newNotification = {
        id: Date.now() + Math.random(),
        ...notification,
        timestamp,
        read: false,
      };
      setNotifications((prev) => [newNotification, ...prev].slice(0, 50));
      if (!silent) {
        addToast({
          type: notification.type || 'info',
          title: notification.title,
          message: notification.message,
          duration: 0,
        });
        if (typeof window !== 'undefined' && window.Notification?.permission === 'granted' && document.hidden) {
          try {
            new window.Notification(notification.title || 'Mrs Cater', {
              body: notification.message || '',
              icon: '/favicon.ico',
            });
          } catch (_) {}
        }
      }
    },
    [addToast]
  );

  const markAsRead = useCallback((notificationId) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const clearAll = useCallback(() => {
    const key = getClearKey();
    if (key && typeof window !== 'undefined') {
      const now = Date.now();
      try {
        window.localStorage.setItem(key, String(now));
      } catch (_) {}
      clearedAtRef.current = now;
    }
    seededRef.current = true;
    setNotifications([]);
  }, [getClearKey]);

  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    const onRefresh = () => fetchUnreadCount();
    window.addEventListener('caterer-notifications-refresh', onRefresh);
    return () => window.removeEventListener('caterer-notifications-refresh', onRefresh);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (!isAuthenticated || !currentUserId) {
      setNotifications([]);
      seededRef.current = false;
      clearedAtRef.current = 0;
      return;
    }
    clearedAtRef.current = loadClearedAt();

    const platformChannel = catererRealtimeOperations.subscribeToPlatformNotifications(
      (row) => {
        addNotification({
          type: 'info',
          title: row?.title || 'Update from Admin',
          message: row?.body || '',
          data: row,
        });
      },
    );

    const messagesChannel = catererRealtimeOperations.subscribeToMessages(
      currentUserId,
      async (payload) => {
        const msg = payload?.new;
        if (!msg) return;
        let fromAdmin = false;
        try {
          fromAdmin = await catererMessagingOperations.isUserAdmin(msg.sender_id);
        } catch (err) {
          console.warn('[Notifications] isUserAdmin check failed', err);
        }
        addNotification({
          type: 'message',
          title: fromAdmin ? 'New Admin Message' : 'New Message',
          message: fromAdmin ? 'You have a new message from Admin' : 'You have a new message from a customer',
          data: msg,
        });
      },
    );

    let ordersChannel = null;
    let bidsChannel = null;
    let eventsChannel = null;
    if (currentCatererId) {
      ordersChannel = catererRealtimeOperations.subscribeToOrdersForCaterer(
        currentCatererId,
        (payload) => {
          const newOrder = payload?.new;
          const oldOrder = payload?.old;
          const eventType = payload?.eventType ?? payload?.event_type ?? (oldOrder == null || oldOrder === undefined ? 'INSERT' : 'UPDATE');

          if (eventType === 'INSERT') {
            if (!newOrder || newOrder.payment_status !== 'paid') return;
            addNotification({
              type: 'order',
              title: 'New order',
              message: 'You have a new order from a customer (payment secured).',
              data: newOrder,
            });
            return;
          }

          if (eventType === 'UPDATE' && newOrder) {
            const newStatus = newOrder.status;
            const oldStatus = oldOrder?.status;
            if (newStatus === oldStatus) return;

            if (newStatus === 'ready_for_delivery') {
              addNotification({
                type: 'order',
                title: 'Order ready for delivery',
                message: 'An order is ready. Ask the customer for the delivery code to confirm handover.',
                data: newOrder,
              });
              return;
            }
            if (newStatus === 'completed') {
              addNotification({
                type: 'order',
                title: 'Order delivered',
                message: 'Delivery confirmed. Payment will be released to you automatically.',
                data: newOrder,
              });
              return;
            }
            if (['confirmed', 'in_progress'].includes(newStatus)) {
              const labels = { confirmed: 'Order accepted', in_progress: 'Preparation started' };
              addNotification({
                type: 'order',
                title: labels[newStatus] || 'Order updated',
                message: newStatus === 'confirmed' ? 'A customer order has been confirmed.' : 'An order is now being prepared.',
                data: newOrder,
              });
            }
          }
        }
      );
      bidsChannel = catererRealtimeOperations.subscribeToBidsForCaterer(
        currentCatererId,
        (payload) => {
          if (payload?.new?.caterer_id === currentCatererId) return;
          addNotification({
            type: 'bid',
            title: 'Bid activity',
            message: 'There is activity on a bid',
            data: payload.new,
          });
        }
      );
      eventsChannel = catererRealtimeOperations.subscribeToNewEvents((payload) => {
        addNotification({
          type: 'event',
          title: 'New special request',
          message: 'A customer has sent a new request you can bid on',
          data: payload.new,
        });
      });
    }

    fetchUnreadCount();

    return () => {
      if (platformChannel) catererRealtimeOperations.unsubscribe(platformChannel);
      if (messagesChannel) catererRealtimeOperations.unsubscribe(messagesChannel);
      if (ordersChannel) catererRealtimeOperations.unsubscribe(ordersChannel);
      if (bidsChannel) catererRealtimeOperations.unsubscribe(bidsChannel);
      if (eventsChannel) catererRealtimeOperations.unsubscribe(eventsChannel);
    };
  }, [isAuthenticated, currentUserId, currentCatererId, addNotification, fetchUnreadCount]);

  // Seed notifications from recent orders; respect "clear all" so they never resurface on login
  useEffect(() => {
    if (!currentCatererId || !currentUserId || seededRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: orders } = await orderOperations.getAllOrders(currentCatererId, 15);
        if (cancelled || !orders?.length) return;
        // Read clearedAt from localStorage so clear-all persists across sessions and logins
        const clearKey = `caterer_notifications_cleared_at_${currentUserId}`;
        let clearedAt = 0;
        try {
          const raw = typeof window !== 'undefined' ? window.localStorage.getItem(clearKey) : null;
          if (raw) clearedAt = Number(raw);
          if (!Number.isFinite(clearedAt)) clearedAt = 0;
        } catch (_) {}
        seededRef.current = true;
        const seeded = orders
          .filter((o) => o.payment_status === 'paid')
          .filter((o) => {
            if (!clearedAt) return true;
            const createdAt = o.created_at ? new Date(o.created_at).getTime() : 0;
            return createdAt > clearedAt;
          })
          .slice(0, 10)
          .map((order) => ({
            id: `seed-${order.id}`,
            type: 'order',
            title: 'New order',
            message: 'You have a new order from a customer (payment secured).',
            timestamp: new Date(order.created_at || Date.now()),
            read: false,
            data: order,
          }));
        setNotifications((prev) => {
          const existingIds = new Set(prev.map((n) => n.id));
          const newSeeded = seeded.filter((s) => !existingIds.has(s.id));
          if (newSeeded.length === 0) return prev;
          return [...newSeeded, ...prev].slice(0, 50);
        });
      } catch (err) {
        console.warn('[Notifications] Seed from orders failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [currentCatererId, currentUserId]);

  const value = {
    notifications,
    unreadCount,
    isOpen,
    toasts,
    addNotification,
    addToast,
    removeToast,
    markAsRead,
    markAllAsRead,
    clearAll,
    toggleOpen,
    closePanel,
    fetchUnreadCount,
    pushPermission,
    requestPushPermission,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
